/**
 * IssueResolutionCanvas - Main component for visualizing issue resolution
 *
 * Cross-shaped layout with:
 * - Issue at center
 * - Trends at top (Context)
 * - Hypotheses at right (Validate)
 * - Data Elements at left (Measure)
 * - Best Practices at bottom (Solve)
 *
 * Based on the UX design proposal in docs/ISSUE_RESOLUTION_CANVAS_DESIGN.md
 */

import React from 'react';
import { X, RefreshCw, Network, Loader2, ChevronLeft, ArrowDown } from 'lucide-react';
import { IssueCard } from './IssueCard';
import { TrendsSection } from './TrendsSection';
import { HypothesesSection } from './HypothesesSection';
import { DataElementsSection } from './DataElementsSection';
import { BestPracticesSection } from './BestPracticesSection';
import { useIssueRelationships } from './hooks/useIssueRelationships';
import type { Issue, Subdomain } from '../../types/domainPack';

interface IssueResolutionCanvasProps {
  issue: Issue;
  subdomain: Subdomain;
  domainPackId: string;
  onClose: () => void;
  onViewInGraph?: () => void;
  onRefreshRelationships?: () => void;
}

export const IssueResolutionCanvas: React.FC<IssueResolutionCanvasProps> = ({
  issue,
  subdomain,
  domainPackId,
  onClose,
  onViewInGraph,
  onRefreshRelationships,
}) => {
  const { relationships, isLoading, error, refetch } = useIssueRelationships(
    domainPackId,
    issue,
    subdomain
  );

  const handleRefresh = async () => {
    if (onRefreshRelationships) {
      onRefreshRelationships();
    }
    await refetch();
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white border-b border-neutral-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                aria-label="Close canvas"
              >
                <ChevronLeft className="w-5 h-5 text-neutral-600" />
              </button>
              <div>
                <h1 className="text-lg font-bold text-neutral-900">
                  Issue Resolution Canvas
                </h1>
                <p className="text-xs text-neutral-500">
                  {subdomain.name} • {issue.title}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {onViewInGraph && (
                <button
                  onClick={onViewInGraph}
                  className="flex items-center gap-2 px-3 py-2 text-sm text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                >
                  <Network className="w-4 h-4" />
                  View in Graph
                </button>
              )}
              <button
                onClick={handleRefresh}
                disabled={isLoading}
                className="flex items-center gap-2 px-3 py-2 text-sm text-neutral-600 hover:bg-neutral-100 rounded-lg transition-colors disabled:opacity-50"
              >
                {isLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <RefreshCw className="w-4 h-4" />
                )}
                Refresh
              </button>
              <button
                onClick={onClose}
                className="p-2 hover:bg-neutral-100 rounded-lg transition-colors"
                aria-label="Close"
              >
                <X className="w-5 h-5 text-neutral-500" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Canvas Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        {isLoading && !relationships ? (
          <LoadingState />
        ) : error ? (
          <ErrorState error={error} onRetry={refetch} />
        ) : (
          <div className="space-y-4">
            {/* Top Section: Trends (Context) */}
            <TrendsSection trends={relationships?.trends || []} />

            {/* Flow Arrow */}
            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-neutral-300" />
            </div>

            {/* Middle Row: DataElements | Issue | Hypotheses */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 items-stretch">
              {/* Left: Data Elements (Measure) */}
              <div className="lg:col-span-3">
                <DataElementsSection dataElements={relationships?.dataElements || []} />
              </div>

              {/* Center: The Issue */}
              <div className="lg:col-span-6 flex flex-col justify-center">
                <IssueCard issue={issue} />

                {/* Connection indicators */}
                <div className="hidden lg:flex justify-between px-8 mt-4 text-xs text-neutral-400">
                  <span>← Measure</span>
                  <span>Validate →</span>
                </div>
              </div>

              {/* Right: Hypotheses (Validate) */}
              <div className="lg:col-span-3">
                <HypothesesSection hypotheses={relationships?.hypotheses || []} />
              </div>
            </div>

            {/* Flow Arrow */}
            <div className="flex justify-center">
              <ArrowDown className="w-6 h-6 text-neutral-300" />
            </div>

            {/* Bottom Section: Best Practices (Solve) */}
            <BestPracticesSection practices={relationships?.bestPractices || []} />
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {relationships && (
        <div className="sticky bottom-0 bg-white border-t border-neutral-200">
          <div className="max-w-7xl mx-auto px-4 py-3">
            <div className="flex items-center justify-center gap-6 text-sm text-neutral-600">
              <SummaryItem
                label="Trends"
                count={relationships.trends.length}
                color="text-blue-600"
              />
              <SummaryItem
                label="Hypotheses"
                count={relationships.hypotheses.length}
                color="text-amber-600"
              />
              <SummaryItem
                label="Data Elements"
                count={relationships.dataElements.length}
                color="text-purple-600"
              />
              <SummaryItem
                label="Best Practices"
                count={relationships.bestPractices.length}
                color="text-green-600"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Summary item component
interface SummaryItemProps {
  label: string;
  count: number;
  color: string;
}

const SummaryItem: React.FC<SummaryItemProps> = ({ label, count, color }) => (
  <div className="flex items-center gap-2">
    <span className={`font-semibold ${color}`}>{count}</span>
    <span className="text-neutral-500">{label}</span>
  </div>
);

// Loading state component
const LoadingState: React.FC = () => (
  <div className="flex flex-col items-center justify-center py-24">
    <Loader2 className="w-12 h-12 text-primary-500 animate-spin mb-4" />
    <p className="text-neutral-600">Loading relationships...</p>
    <p className="text-sm text-neutral-400 mt-1">
      Fetching connected trends, hypotheses, data elements, and best practices
    </p>
  </div>
);

// Error state component
interface ErrorStateProps {
  error: Error;
  onRetry: () => void;
}

const ErrorState: React.FC<ErrorStateProps> = ({ error, onRetry }) => (
  <div className="flex flex-col items-center justify-center py-24">
    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
      <X className="w-8 h-8 text-red-500" />
    </div>
    <p className="text-neutral-900 font-medium mb-2">Failed to load relationships</p>
    <p className="text-sm text-neutral-500 mb-4 text-center max-w-md">
      {error.message || 'An error occurred while fetching issue relationships'}
    </p>
    <button
      onClick={() => onRetry()}
      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
    >
      <RefreshCw className="w-4 h-4" />
      Try Again
    </button>
  </div>
);

export default IssueResolutionCanvas;
